/* eslint-disable max-lines */
/* eslint-disable complexity */
import { confirmActionWithSuspiciousItems } from 'Cloud/Application/Suspicious';
import { clone as cloneRamda, path } from 'ramda';
import { downloadItem, downloadItemsZip, openShareDialog, publishSocial } from 'reactApp/appHelpers/appHelpers';
import { IS_MOBILE_BROWSER, IS_MOBILE_OR_TABLET_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { renderRenameDialog } from 'reactApp/components/BaseConfirmDialog/BaseInputDialog.helpers';
import { renderEmptyTrashBinDialog } from 'reactApp/components/EmptyTrashBin/EmptyTrashBin.helpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import {
    addItemsFromCloudToAlbumRequest,
    createAndPublishAlbumRequest,
    selectAlbumToAddItemsRequest,
} from 'reactApp/modules/albums/albums.actions';
import { isCreatePublicAlbumsEnabled } from 'reactApp/modules/albums/albums.selector';
import { isAlbum } from 'reactApp/modules/albums/albums.types';
import {
    downloadAttachesByIds,
    downloadAttachItem,
    forwardItems,
    getSendViaMailruLink,
    sendAttachesGa,
} from 'reactApp/modules/attaches/attaches.helpers';
import { AttachesSelectors } from 'reactApp/modules/attaches/attaches.selectors';
import { AttachesItem, EAttachTypes } from 'reactApp/modules/attaches/attaches.types';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getFilesWithFaceIdxs, getFileWithFaceById, getSelectedFaceId } from 'reactApp/modules/faces/faces.selectors';
import {
    addToFavorites,
    cloneRequest,
    createNewItem,
    downloadMobileItem as downloadMobileItemAction,
    mountRequest,
    removeFromFavorites,
    removeRequest,
} from 'reactApp/modules/modifying/modifying.actions';
import { getGoToAndScrollUrl } from 'reactApp/modules/modifying/modifying.helpers';
import { PublishItem } from 'reactApp/modules/modifying/modifying.types';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { PublicFile } from 'reactApp/modules/public/public.types';
import { isPublicAlbumTemporaryUrl } from 'reactApp/modules/public/publicAlbum.helper';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { scrollToHomeItemAction, toggleAll } from 'reactApp/modules/selections/selections.actions';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { settingsController } from 'reactApp/modules/settings/settings.controller';
import { showSnackbarAction } from 'reactApp/modules/snackbar/snackbar.actions';
import { snackbarController } from 'reactApp/modules/snackbar/snackbar.controller';
import { SnackbarTypes } from 'reactApp/modules/snackbar/snackbar.types';
import { setSort } from 'reactApp/modules/sort/sort.module';
import { ESortOder, ESortTypes } from 'reactApp/modules/sort/sort.types';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import {
    filterIdsByAuthors,
    getCurrentFolder as getCurrentFolderAction,
    getIds,
    getItemById,
    getStorageItemById,
    getStorageSelectedItems,
} from 'reactApp/modules/storage/storage.selectors';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { trashbinRestoreAllRequest, trashbinRestoreRequest } from 'reactApp/modules/trashbin/trashbin.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { sendQuotaCleanerGa } from 'reactApp/sections/QuotaLanding/QuotaLanding.helpers';
import { store as reduxStore } from 'reactApp/store';
import { Sort } from 'reactApp/types/Tree';
import { renderAuthModalDialog } from 'reactApp/ui/Mobile/AuthModal/AuthModal.helpers';
import { renderCopyDialog, renderCreateShareDialog, renderMoveDialog } from 'reactApp/ui/SelectFolderDialog/SelectFolderDialog.toolkit';
import { copy as copyToClipboard } from 'reactApp/utils/copyToClipboard';
import { sendKasperskiSnackAnalitics } from 'reactApp/utils/kasperskiSnackGa';
import opener from 'reactApp/utils/opener';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { getEncodePath } from 'reactApp/utils/urlHelper';

import { publishHelper, unPublishHelper } from './publishHelper';

const getSelectedItems = (state) => {
    const selectedIdxs = SelectionsSelectors.getSelectedIdxs(state);
    const selectedFace = getSelectedFaceId(state);
    if (selectedFace && !selectedIdxs.length) {
        return getFilesWithFaceIdxs(state, selectedFace)
            .map((id) => getFileWithFaceById(state, id))
            .filter((item): item is PublicFile => !!item);
    }
    const selectedStorage = SelectionsSelectors.getStorageSelected(state);
    return selectedIdxs
        .map((id) => getStorageItemById(state, selectedStorage || getCurrentStorage(state), id))
        .filter((item): item is CloudItem => !!item);
};

const getCurrentFolder = (state) => {
    const folder = getCurrentFolderAction(state);

    return folder?.id && folder?.id !== ROOT_FOLDER_ID ? folder : null;
};

export const getSelectedItemsOrCurrentFolder = (id?: string, storage?: EStorageType) => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);
    let selectedItems = getSelectedItems(state);
    const item = id && getStorageItemById(state, storage || currentStorage, id);

    if (item) {
        return [item];
    }

    if (!Array.isArray(selectedItems) || selectedItems.length === 0) {
        const folder = getCurrentFolder(state);
        if (folder) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            selectedItems = [folder];
        }
    }

    return selectedItems;
};

const create = (type: string, from: string, showShareOnlyButton?: boolean, gaPrefix = '', inHomeRoot = false): void => {
    reduxStore.dispatch(
        createNewItem({
            type,
            from,
            showShareOnlyButton,
            gaPrefix,
            inHomeRoot,
        })
    );
};

const view = (type: string) => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);

    sendPaymentGa({
        eventCategory: ECategoryGa.toolbar,
        action: 'change_view',
        source: currentStorage,
    });

    settingsController.setViewMode(type);
};

const sort = (name: string) => {
    const sortName = name.split('-');
    const sort: Sort = { type: sortName[0] as ESortTypes, order: sortName[1] as ESortOder };
    const state = reduxStore.getState();
    const folder = getCurrentFolderAction(state);

    reduxStore.dispatch(setSort({ sort, id: folder?.id || ROOT_FOLDER_ID }));
};

const rename = (id?: string): void => {
    const selectedItems = getSelectedItemsOrCurrentFolder(id);

    if (selectedItems?.length !== 1 || !selectedItems?.[0]) {
        return;
    }

    renderRenameDialog(selectedItems[0]);
};

const toggleFavorites = (): void => {
    const state = reduxStore.getState();
    const selectedItems = getSelectedItems(state);

    if (!selectedItems?.length) {
        return;
    }

    const isAllInFav = selectedItems.every((item) => path(['isInFavorites'], item));

    if (isAllInFav) {
        reduxStore.dispatch(removeFromFavorites({ items: selectedItems }));
        return;
    }

    reduxStore.dispatch(addToFavorites({ items: selectedItems, from: 'toolbar' }));
};

const copy = (id?: string, sendAnalytics?: (data?: any) => void): void => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);
    const items = getSelectedItemsOrCurrentFolder(id);

    if (!items?.length) {
        return;
    }

    const onSuccess = () => {
        sendAnalytics?.({
            action: 'copy',
            source: currentStorage,
            count_files: items.length,
            items,
        });
    };

    renderCopyDialog({ items, onSuccess });
};

const download = ({
    useDownloadUrl = false,
    id,
    storage,
}: { useDownloadUrl?: boolean; id?: string; storage?: EStorageType } = {}): void => {
    const state = reduxStore.getState();
    const currentFolder = getCurrentFolderAction(state);
    let selected = getSelectedItems(state);
    const currentStorage = getCurrentStorage(state);
    const item = id && getStorageItemById(state, storage || currentStorage, id);

    if (item && !selected.length) {
        selected = [item];
    }

    const { isAttaches, isFavorites, isSearch, isHome, isPublic, isAlbums } = getStorage(currentStorage);

    if (!currentFolder && !selected?.length) {
        return;
    }

    sendPaymentGa({
        eventCategory: ECategoryGa.toolbar,
        action: 'download_content',
        source: currentStorage,
        count_files: selected.length || 1,
    });

    if (isAttaches && selected?.length) {
        sendAttachesGa('download-from-toolbar', selected.length.toString());

        selected.forEach((item) => sendAttachesGa('download', 'ext' in item ? item.ext : ''));

        if (selected.length === 1) {
            const attachType: string | undefined = path(['attachType'], selected[0]);
            const isCloudAttachType = attachType === EAttachTypes.cloud || attachType === EAttachTypes.cloudStock;

            if (isCloudAttachType) {
                downloadItem({ itemOrId: selected[0], useDownloadUrl, storage: storage || currentStorage });
            } else {
                downloadAttachItem(selected[0] as AttachesItem).then(() => {
                    /**
                     * Показываем снэк только для пабликов и для аттачей(CLOUDWEB-14706)
                     * и самым последним этапом скачивания, что бы не мешался другим модалкам на тача
                     * (Пришлось его продублировать тут, т.к. аттачи на таче скачиваются иначе, чем всё остальное)
                     */
                    snackbarController.showSnackbar({
                        id: 'protected-download',
                        closable: true,
                        title: 'Скачивание началось',
                        text: IS_MOBILE_BROWSER
                            ? 'Файл проверен Касперским и безопасен для вашего смартфона'
                            : 'Файл проверен антивирусом Касперского и безопасен для вашего компьютера',
                        type: SnackbarTypes.protect,
                        onShow: () => {
                            sendKasperskiSnackAnalitics({ action: 'view' });
                        },
                        onClose: () => {
                            sendKasperskiSnackAnalitics({ action: 'close' });
                        },
                    });
                });
            }
        } else {
            sendAttachesGa('download-multiple');
            confirmActionWithSuspiciousItems(cloneRamda(selected), 'download').then(function () {
                downloadAttachesByIds(selected.map((item) => item.id));
            });
        }

        return;
    }

    if ((isFavorites || isSearch) && !selected.length && currentFolder && 'list' in currentFolder) {
        downloadItemsZip({ items: Object.values(currentFolder.list), name: isFavorites ? 'favorites' : 'archive' });
        return;
    }

    if (selected.length === 1) {
        if (selected[0].isFolder) {
            downloadItemsZip({ items: selected, name: selected[0].name });
        } else {
            downloadItem({ itemOrId: selected[0], useDownloadUrl, storage: currentStorage });
        }

        return;
    }

    const name = (isHome || isPublic || isAlbums) && currentFolder && 'name' in currentFolder ? currentFolder?.name : 'archive';
    downloadItemsZip({ items: selected.length ? selected : [currentFolder], name });
};

const viewHistory = (): void => {
    const state = reduxStore.getState();
    const selectedItem = getSelectedItems(state)[0];
    openPopupHelper({ popupName: popupNames.FILE_HISTORY, data: { itemId: selectedItem?.id } });
};

const move = (id?: string, sendAnalytics?: (data?: any) => void): void => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);
    const items = getSelectedItemsOrCurrentFolder(id);

    if (!items.length) {
        return;
    }

    const onSuccess = () => {
        sendAnalytics?.({
            action: 'move_to_folder',
            source: currentStorage,
            count_files: items.length,
            items,
        });
    };

    renderMoveDialog({ items, onSuccess });
};

const remove = (id?: string, sendAnalytics?: (data?: any) => void): void => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);
    const items = getSelectedItemsOrCurrentFolder();
    const item = id && getItemById(state, id);

    const onSuccess = () => {
        sendAnalytics?.({
            action: 'delete',
            source: currentStorage,
            count_files: items.length,
            items,
        });
    };

    reduxStore.dispatch(removeRequest({ items: item ? [item] : items, withRemoveDialog: true, onSuccess }));
};

const selectAll = (ownStorage?: EStorageType): void => {
    const state = reduxStore.getState();
    const storage = ownStorage || getCurrentStorage(state);

    const ids = getIds(state, storage);
    const allIdxs = filterIdsByAuthors(state, ids, storage);
    const allSelectedIdx = getStorageSelectedItems(state, storage);
    const isDeselectAll = allIdxs?.length && allIdxs?.length === allSelectedIdx?.length;

    reduxStore.dispatch(toggleAll({ allIdxs, storage }));

    if (storage === EStorageType.quotaCleaner) {
        const prefix = isDeselectAll ? 'all-files-select-cancel' : 'all-files-select';
        sendQuotaCleanerGa({ action: prefix, dwh: { count_select_files: allIdxs?.length } });
    }

    if (!isDeselectAll) {
        sendPaymentGa({
            eventCategory: ECategoryGa.toolbar,
            action: 'select_all',
            source: storage,
        });
    }
};

const share = (id?: string): void => {
    const state = reduxStore.getState();
    const selectedItems = getSelectedItemsOrCurrentFolder();
    const item = (id && getItemById(state, id)) || (selectedItems.length === 1 && selectedItems[0]);
    const storage = getCurrentStorage(state);
    const { isReactPage } = getStorage(storage);

    if ((item && item.id === '/') || !selectedItems.length) {
        renderCreateShareDialog({
            onSuccess: (item) => reduxStore.dispatch(openShareDialog({ item })),
            isReactPage,
        });
        return;
    }

    reduxStore.dispatch(openShareDialog({ item }));
};

const publish = (publishFrom = '', id?: string, storage?: EStorageType): void => {
    const state = reduxStore.getState();
    const selectedItems = getSelectedItemsOrCurrentFolder(id, storage);
    const item = selectedItems.length === 1 ? selectedItems[0] : undefined;
    const isPublicAlbumEnabled = isCreatePublicAlbumsEnabled(state, selectedItems);

    if (!selectedItems.length || item?.id === '/') {
        return;
    }

    if (isPublicAlbumEnabled && !selectedItems.every(isAlbum)) {
        reduxStore.dispatch(createAndPublishAlbumRequest({ items: selectedItems }));
        return;
    }

    publishHelper({ item, publishFrom, itemStorage: storage });
};

const unPublish = (): void => {
    const state = reduxStore.getState();
    const selectedItems = getSelectedItems(state).filter((item) => path(['weblink'], item)) as PublishItem[];

    if (!selectedItems.length) {
        return;
    }

    unPublishHelper({ items: selectedItems });
};

const restore = (id?: string): void => {
    const state = reduxStore.getState();
    const ids = id ? [id] : SelectionsSelectors.getSelectedIdxs(state);
    reduxStore.dispatch(trashbinRestoreRequest({ ids, from: 'toolbar' }));
};

const restoreAll = (): void => {
    reduxStore.dispatch(trashbinRestoreAllRequest());
};

const clear = (): void => {
    renderEmptyTrashBinDialog();
};

const forward = ({ items }: { items: CloudItem[] | null } = { items: null }): void => {
    const state = reduxStore.getState();
    const storage = SelectionsSelectors.getStorageSelected(state) || getCurrentStorage(state);

    if (storage === EStorageType.attaches) {
        const ids = SelectionsSelectors.getSelectedIdxs(state);
        const items = ids.map((id) => AttachesSelectors.getAttachById(state, id));

        items.forEach((item) => sendAttachesGa('forward', item.ext));

        if (items?.length > 1) {
            sendAttachesGa('forward-multiple');
        }

        forwardItems(ids);
    } else {
        if (!items) {
            items = getSelectedItems(state);
        }

        if ((storage === EStorageType.public || storage === EStorageType.stock) && (!items || items.length <= 1)) {
            const item = items.length ? items[0] : getCurrentFolderAction(state);
            publishSocial('email', item);
            return;
        }

        if (Array.isArray(items)) {
            const mailLink = getSendViaMailruLink(items, storage);
            window.open(mailLink, '_blank');
        }
    }
};

const mount = (): void => {
    const state = reduxStore.getState();
    const selectedItems = getSelectedItems(state);
    const item = selectedItems.length === 1 && selectedItems[0];

    if (!item) {
        return;
    }

    // @ts-ignore
    reduxStore.dispatch(mountRequest({ item }));
};

interface IToolbarClone {
    // source для dwh
    source: string;
    id?: string;
    destination?: string;
    autoClone?: boolean;
    storage?: EStorageType;
    // Не показывать снекбар об успехе клонирования
    noOkSnackbar?: boolean;
}

const clone = ({ id, source, destination, autoClone, storage, noOkSnackbar }: IToolbarClone): void => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);
    const items = getSelectedItemsOrCurrentFolder();
    const item = id && getStorageItemById(state, storage || currentStorage, id);
    const isAnonymous = UserSelectors.isAnonymous(state);
    const isPhone = EnvironmentSelectors.isPhone();

    if (isAnonymous && isPhone) {
        renderAuthModalDialog();
        return;
    }

    reduxStore.dispatch(cloneRequest({ items: item ? [item] : items, source, destination, autoClone, noOkSnackbar }));
};

const shareLink = ({ id, url, useNativeShare = true }: { id?: string; url?: string; useNativeShare?: boolean }): void => {
    const state = reduxStore.getState();
    const storage = getCurrentStorage(state);

    const isNativeShareSupported = IS_MOBILE_OR_TABLET_BROWSER && 'share' in navigator && typeof navigator.share === 'function';

    const currentStorage = isPublicAlbumTemporaryUrl() ? 'album' : storage;

    const href = id ? `${window.location.origin}/${currentStorage}/${id}` : window.location.href;
    const copyUrl = url || href;

    if (isNativeShareSupported && useNativeShare) {
        navigator.share({ url: copyUrl }).catch(() => {});
        return;
    }

    copyToClipboard(copyUrl)
        .then(({ hasBeenCopied }) => {
            if (hasBeenCopied) {
                reduxStore.dispatch(
                    showSnackbarAction({
                        id: 'copy-link',
                        closable: true,
                        text: 'Ссылка успешно скопирована',
                        type: SnackbarTypes.success,
                    })
                );
            }
        })
        .catch(() => {
            reduxStore.dispatch(
                showSnackbarAction({
                    id: 'copy-link',
                    closable: true,
                    text: 'Не удалось скопировать ссылку',
                    type: SnackbarTypes.failure,
                })
            );
        });
};

const downloadMobileItem = () => {
    reduxStore.dispatch(downloadMobileItemAction());
};

const showInParentFolder = ({ folderId, itemId }: { folderId: string; itemId?: string }) => {
    const state = reduxStore.getState();
    const storage = getCurrentStorage(state);

    const { isReactPage } = getStorage(storage);

    if (!isReactPage) {
        const selectedFolderId = folderId === ROOT_FOLDER_ID ? '' : getEncodePath(folderId);
        const url = getGoToAndScrollUrl(selectedFolderId, itemId);

        opener(url, true);
        return;
    }

    reduxStore.dispatch(scrollToHomeItemAction({ parentFolderId: folderId, itemId }));
};

const addToAlbum = () => {
    const state = reduxStore.getState();
    const storage = getCurrentStorage(state);
    const items = getSelectedItems(state) || [];

    const selectedItems = getSelectedItemsOrCurrentFolder();
    const isAlbumItem = selectedItems.length === 1 && isAlbum(selectedItems?.[0]);

    if (storage === EStorageType.albums && isAlbumItem) {
        reduxStore.dispatch(addItemsFromCloudToAlbumRequest({ id: selectedItems?.[0]?.id }));
        return;
    }

    if (!items?.length) {
        return;
    }

    reduxStore.dispatch(selectAlbumToAddItemsRequest({ items }));
};

const createAndPublishAlbum = () => {
    const state = reduxStore.getState();
    const items = getSelectedItems(state);

    reduxStore.dispatch(createAndPublishAlbumRequest({ items }));
};

export const toolbarActions = {
    create,
    view,
    sort,
    rename,
    toggleFavorites,
    copy,
    download,
    viewHistory,
    move,
    remove,
    selectAll,
    share,
    publish,
    unPublish,
    restore,
    restoreAll,
    clear,
    forward,
    mount,
    clone,
    shareLink,
    downloadMobileItem,
    showInParentFolder,
    addToAlbum,
    createAndPublishAlbum,
};
